import type {
  CardSlot,
  OpenWorldEntrySide,
  OpenWorldEntryState,
  OpenWorldMapView,
  OpenWorldPartyAction,
  OpenWorldPartyRefusal,
  OpenWorldPartyView,
} from '@kenocyber/shared';
import { OPEN_WORLD_PARTY_POLL_MS } from '@kenocyber/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  actOnOpenWorldParty,
  enterOpenWorld,
  fetchOpenWorldMap,
  fetchOpenWorldParty,
  fetchOpenWorldState,
  fetchOpenWorldTicket,
  openWorldSocketUrl,
} from '../api/open-world';
import type { GameBridge } from '../game/bridge';
import type { PauseCoordinator } from '../game/pause-coordinator';
import type { HuntSuspension } from '../game/useHuntSession';
import {
  browserOpenWorldSocket,
  type OpenWorldClientState,
  OpenWorldConnection,
} from './connection';
import { openWorldSceneFrame, openWorldSceneFrameSignature } from './scene-frame';

/**
 * **A expedição, como React a vê.**
 *
 * ## Quem é dono de quê
 *
 * Este hook é dono do **socket**, do bilhete e da travessia — porque é ele que
 * tem o cookie da sessão e é ele que sabe pedir uma capacidade. A cena é dona
 * do **teclado, do rato e da câmara** — porque é ela que tem a posição
 * desenhada. Os dois falam pela ponte tipada, e nunca um pelo outro.
 *
 * ## O ciclo
 *
 * `caça → escolher lado → porto → pilotar → atravessar → quadrante perigoso →
 * reconectar → extrair → caça`. Nada disto recarrega a página, e nada disto
 * cria um segundo jogo Phaser: o que muda é qual cena está visível.
 */
export interface OpenWorldSession {
  readonly entry: OpenWorldEntryState | null;
  /**
   * **O lobby desta conta**, ou nada enquanto não foi lido.
   *
   * Lido quando o painel abre e depois de cada ação, e nunca num temporizador:
   * um lobby muda porque alguém carregou num botão, e sondá-lo seria pagar uma
   * leitura por segundo para dizer que nada aconteceu.
   */
  readonly party: OpenWorldPartyView | null;
  /**
   * **O mapa desta conta**, ou nada enquanto não foi lido.
   *
   * A grelha conhecida, os oito portos, onde se está, onde já se esteve, e os
   * aliados da expedição ativa. **Nunca** o conteúdo de um quadrante: ele não
   * chega aqui, e portanto não há nada para esconder ao desenhar.
   */
  readonly map: OpenWorldMapView | null;
  /** Volta a ler o mapa. Chamado ao abri-lo, e por eventos já conhecidos. */
  readonly refreshMap: () => Promise<void>;
  readonly state: OpenWorldClientState;
  readonly busy: boolean;
  readonly error: string | null;
  /** Lê onde a conta está. Chamado ao abrir o painel, e nunca num temporizador. */
  readonly refresh: () => Promise<void>;
  /** Entra por um lado, ou retoma. O servidor decide qual dos dois. */
  readonly enter: (side: OpenWorldEntrySide) => Promise<void>;
  /**
   * **Uma das nove ações de PT.**
   *
   * Uma porta só: as nove devolvem a mesma coisa, e o que a distingue é o campo
   * fechado que a nomeia. Quando a ação for um início que se comprometeu, esta
   * função abre a cena e liga o socket — exatamente como uma entrada solo.
   */
  readonly partyAct: (action: OpenWorldPartyAction) => Promise<void>;
  /** Fecha esta janela. A expedição continua no servidor. */
  readonly leave: () => void;
  /**
   * **A caça está suspensa e nada do mundo aberto está no ecrã** (gate V28I.BZ).
   *
   * Uma ligação que falhou a autorização, ou uma restauração depois de um F5
   * que não conseguiu bilhete. O anfitrião abre o painel de entrada, porque é a
   * única coisa que resta fazer: retomar. Nunca durante a partida, o regresso
   * ou a restauração automática — esses estão a caminho de algum lado.
   */
  readonly stranded: boolean;
  /**
   * **Atravessar uma fronteira.** Sempre individual (V28I.T.1, ADR 0081).
   *
   * Cada nave chega à fronteira por si, escolhe a sua direção e parte sozinha —
   * inclusive quem está numa PT. Aliados podem seguir-se uns aos outros; ninguém
   * transporta ninguém, e sete integrantes que queiram mudar de quadrante são
   * sete travessias independentes.
   */
  readonly requestCrossing: (direction: 'north' | 'south' | 'east' | 'west') => void;
  readonly requestPortEntry: (direction: 'north' | 'south' | 'east' | 'west') => void;
  readonly requestExtraction: () => void;
  readonly cancelBorder: () => void;
  /** Lança a linha. O servidor decide a água e o peixe. */
  readonly startFishing: () => void;
  /**
   * **Abandona a pescaria a decorrer.** O peixe escapa e a vaga só volta pela
   * reposição normal.
   *
   * Não recebe nada: o id da tentativa vem do estado autoritativo
   * (`state.fishing.o`), e não de um contador do navegador — quem reconectou
   * também sabe qual é a sua.
   */
  readonly abandonFishing: () => void;
  /** Pede o balcão do comerciante. */
  readonly openMerchant: () => void;
  /** Vende. `null` em `ids` é o VENDER TODOS daquela secção. */
  readonly sell: (scope: 'field' | 'vault', ids: readonly string[] | null) => void;
  /** Pede a lista da própria carga. */
  readonly inspectCargo: () => void;
  /**
   * **Liga a carta deste espaço.** A ÚNICA fronteira de ordem.
   *
   * O rato e o teclado chamam-na igual, e ela não espera por nada: o estado que
   * a barra desenha vem da autoridade, e muda quando a autoridade responder.
   */
  readonly activateCard: (slot: CardSlot) => void;
  /** Solta a carga de um espaço. As duas metades do mesmo gesto. */
  readonly releaseCard: (slot: CardSlot) => void;
  /** Pede para pilotar um Leviatã. O servidor escolhe qual, e revalida. */
  readonly boardSummon: () => void;
  readonly openBox: (boxId: string) => void;
  readonly closeBox: () => void;
  readonly takeItem: (boxId: string, fieldItemId: string) => void;
  readonly takeAllItems: (boxId: string) => void;
  readonly takeGold: (boxId: string) => void;
}

/**
 * **O que o mundo aberto precisa da caça, e nada mais** (gate V28I.BZ).
 *
 * Partir suspende-a — nenhum checkpoint, nenhum bootstrap — e voltar retoma-a
 * por UM bootstrap. A trela de pausa e o esconder seguem `suspended`, portanto
 * a caça só reaparece depois de a resposta descrever uma corrida jogável.
 */
export interface OpenWorldHuntLink {
  /** Suspensa por uma expedição, dito por quem for: o cliente ou o servidor. */
  readonly suspended: boolean;
  /** A palavra da AUTORIDADE: o último bootstrap disse que a conta está lá. */
  readonly suspendedFor: HuntSuspension | null;
  readonly suspend: () => void;
  readonly resume: () => Promise<void>;
}

export function useOpenWorld(
  bridge: GameBridge,
  pause: PauseCoordinator,
  /**
   * A caça desta conta, pela porta estreita (gate V28I.BZ).
   *
   * **Necessária, e não uma cortesia.** Entrar no mundo aberto ENCERRA a sessão
   * de caça no servidor. Antes deste gate a caça continuava a sincronizar por
   * trás da expedição: o checkpoint seguinte era recusado, o cliente voltava a
   * pedir a caça, e o servidor abria uma corrida nova atrás do mundo aberto a
   * cada meio minuto — duas modalidades, e um F5 restaurava as duas.
   */
  hunt: OpenWorldHuntLink,
): OpenWorldSession {
  const { suspend: suspendHunt, resume: resumeHunt } = hunt;
  const [entry, setEntry] = useState<OpenWorldEntryState | null>(null);
  const [party, setParty] = useState<OpenWorldPartyView | null>(null);
  const [map, setMap] = useState<OpenWorldMapView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** A caminho da caça: o bootstrap do regresso foi pedido e ainda não adotado. */
  const [returning, setReturning] = useState(false);
  /** A restaurar uma expedição que a autoridade diz existir — depois de um F5. */
  const [restoring, setRestoring] = useState(false);
  /**
   * A cena do quadrante já subscreveu a ponte. Até lá um `open-world:open` não
   * chega a ninguém — e é o que a restauração depois de um F5 publica.
   */
  const [sceneReady, setSceneReady] = useState(false);

  const connection = useMemo(
    () =>
      new OpenWorldConnection({
        mintTicket: async (quadrant) => {
          const minted = await fetchOpenWorldTicket(quadrant);
          return minted.ok ? { ticket: minted.ticket, socketUrl: minted.socketUrl } : null;
        },
        socketUrl: openWorldSocketUrl,
        openSocket: browserOpenWorldSocket,
        now: () => Date.now(),
        presentationNow: () => performance.now(),
        schedule: (run, ms) => {
          const handle = window.setTimeout(run, ms);
          return () => window.clearTimeout(handle);
        },
      }),
    [],
  );

  const [state, setState] = useState<OpenWorldClientState>(connection.snapshot);
  useEffect(() => connection.subscribe(setState), [connection]);

  /**
   * **A caça parada e escondida enquanto estiver suspensa, ou enquanto o mundo
   * aberto estiver no ecrã ou a caminho dele.**
   *
   * **Duas coisas, porque são duas perguntas.** A pausa pára o relógio da caça;
   * esconder pára o desenho — uma caça parada continua a pintar o seu último
   * frame no canvas que as duas cenas partilham. É exatamente o que um duelo
   * faz, e pela mesma razão.
   *
   * **Um efeito, e não passos espalhados** (gate V28I.BZ). Antes, cada saída —
   * partir, a expedição acabar, voltar — pegava e largava a trela à mão, e o
   * fim da expedição largava-a com o relatório ainda no ecrã: a caça voltava a
   * correr com a sessão que a entrada tinha fechado, e o checkpoint seguinte era
   * um 409. Agora a trela segue o estado, e a caça só reaparece quando a
   * resposta do regresso descreve uma corrida jogável.
   *
   * Uma cena criada DEPOIS disto — um F5 — subscreve mais tarde, e a ponte não
   * repete mensagens: o `game:ready` dela é o primeiro instante em que ouve, e
   * é aí que o esconder se repete. A pausa já é repetida pelo coordenador.
   */
  const huntHeld = hunt.suspended || (state.phase !== 'idle' && state.phase !== 'failed');
  useEffect(() => {
    if (!huntHeld) {
      return;
    }
    const lease = pause.acquire('open_world');
    const hide = () => bridge.sendToGame('hunt:visible', { visible: false });
    hide();
    const offReady = bridge.onUiMessage('game:ready', hide);
    return () => {
      offReady();
      bridge.sendToGame('hunt:visible', { visible: true });
      lease();
    };
  }, [bridge, huntHeld, pause]);

  useEffect(
    () => bridge.onUiMessage('open-world:scene-ready', () => setSceneReady(true)),
    [bridge],
  );

  /**
   * **O comando da cena, encaminhado para o socket.**
   *
   * A cena lê o teclado e o rato porque tem a câmara; este hook envia porque
   * tem a ligação. Uma ponte tipada entre os dois, e nenhum a atravessar para
   * dentro do outro.
   */
  useEffect(
    () => bridge.onUiMessage('open-world:command', (intent) => connection.sendCommand(intent)),
    [bridge, connection],
  );

  /**
   * **O frame autoritativo, empurrado para a cena.**
   *
   * Empurrado a cada mudança de estado, e não a cada frame de ecrã: o que muda
   * é o que o servidor disse, e o servidor diz a 20 Hz. A cena interpola entre
   * dois destes, que é o que faz sessenta frames a partir de vinte.
   */
  const lastFrame = useRef('');
  useEffect(() => {
    if (state.phase !== 'live' && state.phase !== 'crossing') {
      return;
    }
    const payload = openWorldSceneFrame(state);
    const signature = openWorldSceneFrameSignature(state, payload);
    if (signature === lastFrame.current) {
      return;
    }
    lastFrame.current = signature;
    bridge.sendToGame('open-world:frame', payload);
  }, [bridge, state]);

  /**
   * **A revisão da vista da PT, e ela existe para uma corrida.**
   *
   * Duas leituras podem estar no ar ao mesmo tempo — a do relógio e a que uma
   * ação disparou — e a rede não promete que chegam pela ordem em que saíram.
   * Cada pedido leva o número da sua revisão; uma resposta cuja revisão já foi
   * ultrapassada é lida e deitada fora, em vez de repor uma composição que já
   * mudou.
   */
  const partyRevision = useRef(0);

  /**
   * **Lê o lobby, e só aplica se ainda for a leitura mais recente.**
   *
   * Uma falha não apaga o que já se sabe: um quadrante que responde tarde não
   * é uma PT que se desfez, e esvaziar a lista faria a interface anunciar uma
   * coisa que não aconteceu.
   */
  const refreshParty = useCallback(async () => {
    partyRevision.current += 1;
    const revision = partyRevision.current;
    try {
      const next = await fetchOpenWorldParty();
      if (revision !== partyRevision.current) {
        return;
      }
      setParty(next);
    } catch {
      /* Mantém a última vista boa. */
    }
  }, []);

  /**
   * **A revisão do mapa, e ela é dupla.**
   *
   * A do PEDIDO impede que uma resposta lenta reponha uma vista que já foi
   * substituída por outra leitura desta aba. A da RESPOSTA — o mundo e a
   * época que o servidor afirmou — impede que uma resposta que descreve um
   * passado reponha uma coordenada nova: a época sobe exatamente nas escritas
   * que movem a conta, portanto uma resposta com época menor é uma resposta
   * de antes da travessia.
   *
   * Um mundo diferente aceita-se sempre: a época recomeça do zero num mundo
   * novo, e compará-la entre mundos seria recusar o mapa novo por ele parecer
   * mais antigo.
   */
  const mapRequest = useRef(0);
  const mapRevision = useRef<{ world: string; epoch: number } | null>(null);

  /**
   * **Lê o mapa, e só aplica se ainda for a leitura mais recente.**
   *
   * Uma falha não apaga a última vista boa. Um mapa é memória, e apagar o que
   * um piloto já descobriu por causa de uma rede lenta seria a interface a
   * afirmar que ele nunca esteve ali.
   */
  const refreshMap = useCallback(async () => {
    mapRequest.current += 1;
    const request = mapRequest.current;
    try {
      const next = await fetchOpenWorldMap();
      if (request !== mapRequest.current) {
        return;
      }
      const seen = mapRevision.current;
      if (seen !== null && seen.world === next.revision.world && next.revision.epoch < seen.epoch) {
        /* Uma resposta de antes da travessia. Lida, e deitada fora. */
        return;
      }
      mapRevision.current = { world: next.revision.world, epoch: next.revision.epoch };
      setMap(next);
    } catch {
      /* Mantém a última vista boa. */
    }
  }, []);

  /**
   * **A leitura mais recente do estado da entrada, e só ela** (gate V28I.BZ).
   *
   * O estado da entrada envelhece: uma expedição que acabou deixava para trás
   * um `active` que já não existe, e o painel desenhava-o no instante em que
   * abria — RETOMAR durante um frame, antes de a leitura nova chegar. Sair de
   * uma expedição apaga-o e sobe esta revisão; uma resposta pedida antes disso
   * chega, é lida, e é deitada fora.
   */
  const entryRead = useRef(0);
  const readEntry = useCallback(async (): Promise<OpenWorldEntryState> => {
    entryRead.current += 1;
    const read = entryRead.current;
    const next = await fetchOpenWorldState();
    if (read === entryRead.current) {
      setEntry(next);
    }
    return next;
  }, []);

  const refresh = useCallback(async () => {
    try {
      await readEntry();
      setError(null);
    } catch {
      setError('Não foi possível ler o estado do mundo aberto.');
    }
    /*
     * O lobby vem a seguir e numa tentativa própria: um mundo aberto desligado
     * responde com um lobby vazio, e uma falha a lê-lo não deve apagar o que já
     * se sabe sobre a entrada.
     */
    await refreshParty();
  }, [readEntry, refreshParty]);

  /**
   * **De volta à caça, por UM bootstrap** (gate V28I.BZ).
   *
   * `returning` cobre o instante entre o pedido e a adoção: sem ele, a caça
   * ainda suspensa e o mundo aberto já fechado pareciam uma conta encalhada, e
   * o painel de entrada abria-se no caminho de volta.
   */
  const returnToHunt = useCallback(async () => {
    setReturning(true);
    try {
      await resumeHunt();
    } finally {
      setReturning(false);
    }
  }, [resumeHunt]);

  const enter = useCallback(
    async (side: OpenWorldEntrySide) => {
      setBusy(true);
      setError(null);
      /*
       * **A caça pára ANTES do pedido** (gate V28I.BZ). É a entrada que fecha a
       * sessão no servidor, e um checkpoint que corresse com ela voltava
       * recusado e pedia uma caça nova por trás da expedição. Uma recusa devolve
       * a caça pelo caminho normal: UM bootstrap, que diz onde a conta está.
       */
      suspendHunt();
      let departed = false;
      try {
        const result = await enterOpenWorld(side);
        if (!result.ok) {
          setError(refusalText(result.refusal));
          return;
        }
        /*
         * A cena abre ANTES do socket. Ela nasce vazia e calada, e um quadrante
         * que aparece um instante antes da primeira trama é melhor do que uma
         * trama que chega antes de haver onde a desenhar.
         */
        bridge.sendToGame('open-world:open', {});
        connection.connect(result.quadrant, {
          ticket: result.ticket,
          socketUrl: result.socketUrl,
        });
        departed = true;
      } catch {
        setError('Não foi possível entrar no mundo aberto.');
      } finally {
        setBusy(false);
        if (!departed) {
          void returnToHunt();
        }
      }
    },
    [bridge, connection, returnToHunt, suspendHunt],
  );

  /**
   * **O relógio que faz a lista da PT convergir** (gate V28I.Q.1).
   *
   * ## Porque existe um, e porque é lento
   *
   * Onde um aliado está muda quando ele atravessa uma fronteira, morre ou
   * extrai — três coisas que acontecem a cada minutos, e nenhuma delas a cada
   * quadro. A pergunta que esta lista responde é "para onde vou para o
   * encontrar", e ela não muda entre dois tiques do jogo.
   *
   * **Não há um pedido por quadro, por movimento ou por tiro.** O socket do
   * quadrante continua a ser quem descreve o combate; isto é uma leitura
   * periódica de uma vista pequena, e ela é a única.
   *
   * ## Quando NÃO corre
   *
   * Sem PT, ou numa PT de um só integrante sem expedição: não há nada para
   * convergir, e um relógio a bater sobre nada é tráfego a troco de nada.
   *
   * ## A aba escondida
   *
   * O intervalo é DESMONTADO, e não apenas ignorado: um separador em segundo
   * plano não deve manter conversa com o servidor. Ao voltar, uma leitura
   * imediata — porque o que se perdeu enquanto esteve escondido é exatamente o
   * que interessa ver primeiro.
   */
  const partyLive =
    party?.party != null && (party.party.members.length > 1 || party.party.expeditionActive);
  useEffect(() => {
    if (!partyLive) {
      return;
    }
    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const start = () => {
      if (timer === null) {
        timer = setInterval(() => void refreshParty(), OPEN_WORLD_PARTY_POLL_MS);
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshParty();
        start();
        return;
      }
      stop();
    };
    document.addEventListener('visibilitychange', onVisibility);
    if (document.visibilityState === 'visible') {
      start();
    }
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      stop();
    };
  }, [partyLive, refreshParty]);

  /**
   * **E uma leitura imediata quando um evento local já se sabe.**
   *
   * Mudar de quadrante e perder ou ganhar o socket são as três coisas que este
   * cliente descobre sem perguntar a ninguém. Esperar pelo relógio depois de
   * uma delas seria mostrar durante segundos uma lista que já se sabe errada.
   */
  const localSignature = `${state.label}:${state.phase}`;
  /**
   * **O que da PT faz o mapa mudar**, e nada mais.
   *
   * Quem, onde e em que estado. O nome não entra: um aliado que mude de nome
   * não mudou de quadrante, e voltar a ler o mapa por causa disso seria uma
   * consulta a troco de nada.
   */
  const partySignature = (party?.party?.members ?? [])
    .map((member) =>
      [
        member.id,
        member.state,
        member.quadrant === null
          ? '-'
          : `${member.quadrant.rowIndex}.${member.quadrant.columnIndex}`,
      ].join('~'),
    )
    .join('|');
  const lastLocalSignature = useRef('');
  useEffect(() => {
    if (!partyLive) {
      return;
    }
    /*
     * A comparação é explícita para que a leitura aconteça pela mudança e não
     * por um render: a PT entrar em expedição não é uma travessia, e pedir a
     * vista outra vez por causa disso seria uma consulta a troco de nada.
     */
    if (lastLocalSignature.current === localSignature) {
      return;
    }
    lastLocalSignature.current = localSignature;
    void refreshParty();
  }, [localSignature, partyLive, refreshParty]);

  /**
   * **Quando o mapa volta a ser lido, e nunca num relógio próprio.**
   *
   * O mapa muda por eventos, e todos eles já são conhecidos deste lado:
   *
   * - **uma mudança local** — entrar, atravessar, reconectar, extrair — que a
   *   assinatura do quadrante e da fase descreve inteira;
   * - **uma revisão nova da PT**, que é onde a posição dos aliados muda. Ela já
   *   é lida no relógio conservador do V28I.Q.1, e o mapa apanha boleia dele em
   *   vez de abrir uma segunda sondagem;
   * - **a aba voltar a ficar visível**, uma vez e sem intervalo;
   * - **abrir o mapa**, que o componente pede quando monta.
   *
   * Não há aqui pedido por quadro nem por movimento, e não há um segundo
   * temporizador: o mapa é memória, e memória não se sonda.
   */
  const mapSignature = `${localSignature}:${partySignature}`;
  const lastMapSignature = useRef('');
  useEffect(() => {
    if (lastMapSignature.current === mapSignature) {
      return;
    }
    lastMapSignature.current = mapSignature;
    void refreshMap();
  }, [mapSignature, refreshMap]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void refreshMap();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshMap]);

  /**
   * **Uma ação de PT**, e a entrada coletiva quando ela é um início.
   *
   * A vista volta sempre, mesmo numa recusa: o servidor recalcula-a a cada
   * ação, e mostrar o que ele disse é a única forma de a interface não ter uma
   * segunda opinião sobre quem está pronto.
   *
   * Quando a resposta traz um bilhete, o início comprometeu-se: a cena abre e o
   * socket liga, exatamente como numa entrada solo. Nada aqui decide que a
   * expedição existe — isso já foi decidido por uma escrita do outro lado.
   */
  const partyAct = useCallback(
    async (action: OpenWorldPartyAction) => {
      setBusy(true);
      setError(null);
      /*
       * **Um início pára a caça antes do pedido**, como uma entrada solo e pela
       * mesma razão (gate V28I.BZ): é ele que fecha as caças da PT no servidor.
       * As outras ações não mexem na caça.
       */
      const starting = action.do === 'start';
      if (starting) {
        suspendHunt();
      }
      let departed = false;
      try {
        const answer = await actOnOpenWorldParty(action);
        setParty(answer.view);
        if (!answer.ok) {
          setError(partyRefusalText(answer.refusal));
          return;
        }
        if (answer.ticket === undefined) {
          return;
        }
        const at = answer.view.party;
        if (at === null) {
          return;
        }
        /*
         * O quadrante vem da leitura do estado, que a entrada acabou de mudar:
         * o bilhete nomeia um sítio, e é ele que o socket usa.
         */
        const where = await readEntry();
        if (where.active === null) {
          return;
        }
        bridge.sendToGame('open-world:open', {});
        /*
         * A resposta da PT não carrega destino: uma partida em grupo é rara e
         * não está no caminho quente, portanto vale mais uma ida extra ao
         * servidor do que um terceiro sítio a escolher autoridades.
         */
        connection.connect(where.active.quadrant, { ticket: answer.ticket, socketUrl: null });
        departed = true;
      } catch {
        setError('Não foi possível falar com a PT.');
      } finally {
        setBusy(false);
        if (starting && !departed) {
          void returnToHunt();
        }
      }
    },
    [bridge, connection, readEntry, returnToHunt, suspendHunt],
  );

  /**
   * **O relatório foi lido: de volta à caça.**
   *
   * O socket fecha, a cena do quadrante larga tudo, e a caça é pedida ao
   * servidor por UM bootstrap. Ela só volta a desenhar-se quando a resposta for
   * adotada: a trela e o esconder seguem `hunt.suspended`, que só cai com uma
   * corrida jogável. Uma expedição fechou a sessão anterior; a que existe agora
   * é outra.
   */
  const leave = useCallback(() => {
    connection.disconnect();
    bridge.sendToGame('open-world:close', {});
    /*
     * **O estado da entrada morre com a expedição** (gate V28I.BZ). Ele dizia
     * onde a conta estava ANTES — um `active` que acabou de deixar de existir —
     * e o painel desenhava-o no instante em que voltava a abrir: RETOMAR durante
     * um frame. Apagado aqui, e com a revisão a subir para uma leitura ainda a
     * caminho não o repor.
     */
    entryRead.current += 1;
    setEntry(null);
    void returnToHunt();
  }, [bridge, connection, returnToHunt]);

  /*
   * A expedição acabou — extraída ou morta. A cena fecha, e o recibo fica no
   * estado para ser mostrado: quem perdeu a ligação a meio recebe-o outra vez
   * ao reconectar, porque um recibo é uma linha que já existe no servidor.
   *
   * **A caça NÃO volta aqui** (gate V28I.BZ). O relatório ainda está no ecrã, e
   * a caça por trás dele descreveria a sessão que a entrada fechou: largar a
   * trela neste instante punha-a a correr, e o checkpoint seguinte era um 409
   * que pedia outra caça antes de o jogador ter voltado. Ela volta no `leave`.
   */
  useEffect(() => {
    if (state.phase === 'ended') {
      bridge.sendToGame('open-world:close', {});
    }
  }, [bridge, state.phase]);

  /**
   * **Restaura a expedição que a autoridade diz existir** (gate V28I.BZ).
   *
   * Um F5 a meio de uma expedição — ou um integrante de PT cuja caça o início
   * fechou — recebe da caça uma resposta suspensa: a conta está no mundo aberto.
   * O que se restaura é essa modalidade, e só essa, pelo caminho de uma
   * reconexão: um bilhete para o quadrante onde a conta ESTÁ, que nunca cria
   * expedição nenhuma. Se ela acabou entretanto, a caça volta por um bootstrap.
   */
  const restoreExpedition = useCallback(async () => {
    setRestoring(true);
    setError(null);
    try {
      const where = await readEntry();
      if (where.active === null) {
        await returnToHunt();
        return;
      }
      const minted = await fetchOpenWorldTicket(where.active.quadrant);
      if (!minted.ok) {
        if (minted.refusal === 'not_in_world') {
          await returnToHunt();
          return;
        }
        setError('Não foi possível retomar a expedição. Tente outra vez.');
        return;
      }
      bridge.sendToGame('open-world:open', {});
      connection.connect(where.active.quadrant, {
        ticket: minted.ticket,
        socketUrl: minted.socketUrl,
      });
    } catch {
      setError('Não foi possível retomar a expedição. Tente outra vez.');
    } finally {
      setRestoring(false);
    }
  }, [bridge, connection, readEntry, returnToHunt]);

  /*
   * **Uma vez por declaração da autoridade**, e só com a cena a ouvir. Uma
   * restauração recusada não se repete sozinha: o painel abre com a razão e o
   * RETOMAR, e é a pessoa que decide tentar outra vez.
   */
  const [restoreAttempted, setRestoreAttempted] = useState(false);
  useEffect(() => {
    if (hunt.suspendedFor === null) {
      setRestoreAttempted(false);
      return;
    }
    if (restoreAttempted || !sceneReady || busy || returning || state.phase !== 'idle') {
      return;
    }
    setRestoreAttempted(true);
    void restoreExpedition();
  }, [
    busy,
    hunt.suspendedFor,
    restoreAttempted,
    restoreExpedition,
    returning,
    sceneReady,
    state.phase,
  ]);

  /*
   * Suspensa, e nada do mundo aberto no ecrã nem a caminho dele: nem a partida,
   * nem o regresso, nem uma restauração por fazer ou a decorrer.
   */
  const stranded =
    hunt.suspended &&
    !(hunt.suspendedFor !== null && !restoreAttempted) &&
    !busy &&
    !returning &&
    !restoring &&
    (state.phase === 'idle' || state.phase === 'failed');

  return {
    entry,
    party,
    map,
    refreshMap,
    state,
    busy,
    error,
    refresh,
    partyAct,
    enter,
    leave,
    stranded,
    requestCrossing: useCallback(
      (direction) => connection.requestCrossing(direction),
      [connection],
    ),
    requestPortEntry: useCallback(
      (direction) => connection.requestPortEntry(direction),
      [connection],
    ),
    requestExtraction: useCallback(() => connection.requestExtraction(), [connection]),
    cancelBorder: useCallback(() => connection.cancelBorder(), [connection]),
    startFishing: useCallback(() => {
      connection.startFishing();
    }, [connection]),
    abandonFishing: useCallback(() => {
      const own = connection.snapshot.fishing;
      if (own !== null) {
        connection.abandonFishing(own.o);
      }
    }, [connection]),
    openMerchant: useCallback(() => connection.openMerchant(), [connection]),
    inspectCargo: useCallback(() => connection.inspectCargo(), [connection]),
    activateCard: useCallback(
      (slot: CardSlot) => {
        connection.activateCard(cardSlotNumber(slot));
      },
      [connection],
    ),
    releaseCard: useCallback(
      (slot: CardSlot) => {
        connection.releaseCard(cardSlotNumber(slot));
      },
      [connection],
    ),
    boardSummon: useCallback(() => {
      connection.boardSummon();
    }, [connection]),
    openBox: useCallback(
      (boxId: string) => {
        connection.openBox(boxId);
      },
      [connection],
    ),
    closeBox: useCallback(() => connection.closeBox(), [connection]),
    takeItem: useCallback(
      (boxId: string, fieldItemId: string) => {
        connection.takeItem(boxId, fieldItemId);
      },
      [connection],
    ),
    takeAllItems: useCallback(
      (boxId: string) => {
        connection.takeAllItems(boxId);
      },
      [connection],
    ),
    takeGold: useCallback(
      (boxId: string) => {
        connection.takeGold(boxId);
      },
      [connection],
    ),
    sell: useCallback(
      (scope: 'field' | 'vault', ids: readonly string[] | null) => {
        connection.sell(scope, ids);
      },
      [connection],
    ),
  };
}

/** Uma recusa, em português e sem jargão. O código fica para o log. */
function refusalText(refusal: string): string {
  switch (refusal) {
    case 'in_duel':
      return 'Termine o duelo antes de partir para o mundo aberto.';
    case 'already_in_world':
      return 'Já existe uma expedição a decorrer.';
    case 'receipt_pending':
      return 'Há um relatório de expedição por ler.';
    case 'hunt_busy':
      return 'A caça não pôde ser fechada. Tente outra vez.';
    case 'world_full':
      return 'QUADRANTE LOTADO — não há vaga disponível neste quadrante. Tente outra vez daqui a pouco.';
    case 'world_misconfigured':
      return 'O mundo aberto não está configurado nesta instalação. Nada foi alterado.';
    default:
      return 'O mundo aberto não está disponível.';
  }
}

/**
 * O número de um espaço, a partir do nome canónico.
 *
 * A barra fala pelos nomes que o inventário usa; o protocolo conta bytes e diz
 * 1, 2 ou 3. A tradução vive aqui, uma vez, na fronteira entre os dois.
 */
function cardSlotNumber(slot: CardSlot): 1 | 2 | 3 {
  switch (slot) {
    case 'card_slot_1':
      return 1;
    case 'card_slot_2':
      return 2;
    default:
      return 3;
  }
}

/**
 * Porque a PT recusou, em português e nunca um código.
 *
 * Exaustiva sobre a lista fechada: uma recusa nova não compila sem uma frase, e
 * um jogador nunca vê um identificador técnico.
 */
function partyRefusalText(refusal: OpenWorldPartyRefusal | null): string {
  switch (refusal) {
    case 'already_in_party':
      return 'Já está numa PT.';
    case 'not_in_party':
      return 'Não está em nenhuma PT.';
    case 'not_leader':
      return 'Só o líder pode fazer isso.';
    case 'party_full':
      return 'A PT está cheia.';
    case 'invite_expired':
      return 'Esse convite já não é válido.';
    case 'invite_failed':
      return 'Não foi possível convidar esse jogador.';
    case 'expedition_active':
      return 'A expedição está a decorrer: a PT está congelada.';
    case 'not_ready':
      return 'Nem toda a gente está pronta.';
    case 'member_busy':
      return 'Alguém da PT não pode entrar agora.';
    case 'formation_full':
      return 'A formação não cabe nesse porto.';
    case 'quadrant_full':
      return 'QUADRANTE LOTADO — não há vagas para a PT inteira. Tente outra vez daqui a pouco.';
    case 'rate_limited':
      return 'Demasiados convites. Espere um pouco.';
    default:
      return 'O mundo aberto não está disponível.';
  }
}
